/**
 * Utility functions for handling coastal distance zone labels.
 * Provides parsing and sorting functionality for distance-based labels
 * in formats like "0 ft - 500 ft", "500 ft - 1 Mile", etc.
 */

export interface ParsedDistance {
    value: number;
    unit: string;
    originalValue: string;
}

export class CoastalDistanceUtils {
    private static readonly FEET_PER_MILE = 5280;
    private static readonly DISTANCE_PATTERN = /(\d+(?:\.\d+)?)\s*(feet|foot|ft|mile|miles|mi)/i;
    private static readonly MILE_UNITS = new Set(['mile', 'miles', 'mi']);

    /**
     * Parses a distance string and extracts the numeric value and unit.
     * Handles formats like "500 ft", "1 Mile", "2 Miles", "500 feet", etc.
     * For range labels like "0 ft - 500 ft", extracts the upper bound.
     */
    public static parseDistance(value: string): ParsedDistance | null {
        if (!value || typeof value !== 'string') return null;

        const trimmed = value.trim();
        
        // For range format, use the upper bound (second part) for sorting
        const dashIndex = trimmed.indexOf(' - ');
        const parseTarget = dashIndex !== -1 ? trimmed.substring(dashIndex + 3).trim() : trimmed;
        
        const match = parseTarget.match(this.DISTANCE_PATTERN);
        if (match) {
            return {
                value: parseFloat(match[1]),
                unit: match[2].toLowerCase(),
                originalValue: trimmed
            };
        }
        
        return null;
    }

    /**
     * Converts a parsed distance to feet for comparison.
     */
    public static toFeet(parsed: ParsedDistance | null): number {
        if (!parsed) return Infinity;
        return this.MILE_UNITS.has(parsed.unit) ? parsed.value * this.FEET_PER_MILE : parsed.value;
    }

    /**
     * Compares two distance values. Returns negative if a < b, positive if a > b, 0 if equal.
     * Used for sorting in analytics components.
     */
    public static compareDistances(a: string, b: string, isAsc: boolean = true): number {
        const feetA = this.toFeet(this.parseDistance(a));
        const feetB = this.toFeet(this.parseDistance(b));
        return (feetA - feetB) * (isAsc ? 1 : -1);
    }

    /**
     * Sorts an array of coastal distance labels in ascending order by distance.
     */
    public static sortCoastalDistanceValues(values: string[]): string[] {
        if (!values || values.length === 0) return values;
        return [...values].sort((a, b) => this.compareDistances(a, b));
    }

    /**
     * Checks if a value looks like a coastal distance label.
     */
    public static isDistanceLabel(value: string): boolean {
        if (!value || typeof value !== 'string') return false;
        return this.DISTANCE_PATTERN.test(value);
    }

    /**
     * Checks if an array of values contains coastal distance labels.
     */
    public static areDistanceLabels(values: string[]): boolean {
        if (!values || values.length === 0) return false;
        const distanceCount = values.filter(v => this.isDistanceLabel(v)).length;
        return distanceCount >= values.length / 2;
    }
}
